import { Component, DestroyRef, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CfiRecordEditComponent, UserRole } from '@k12/app-components';
import { StudentAwardService } from '@k12/app-components';
import { SOS_SECTIONS, SOS_OPTIONS } from '../../student-os/student-os.config';
import { FieldOption, HouseholdStudent } from '@k12/models';
import { CountiesService } from '@k12/school-services';
import { LoadingSpinnerService, AppInsightsService } from '@k12/app-services';
import { ApplicationStatus, AwardStatus, isAwardStatus, StudentOs } from '@k12/models';
import { IneligibilityReason } from '../../../awards/models/ineligibilityReason.model';
import { confirmIneligibleTaskCancellation, omitUnchangedEligibility, requireReasonWhenIneligible, withSavedOs } from '../../application-eligibility.rule';
import { ConfirmDialogService } from '@k12/ui-primitives';
import { filter } from 'rxjs';

@Component({
  selector: 'lib-student-os-detail-edit',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [ReactiveFormsModule, CfiRecordEditComponent],
  template: `
    <cfi-record-edit
      [title]="studentName"
      [sections]="sections"
      [form]="form"
      [options]="options"
      [userRole]="userRole"
      pageTitle="Edit Opportunity Scholarship"
      prefix="sos"
      (save)="onSave()"
      (cancelled)="onCancel()" />
  `
})
export class StudentOsDetailEditComponent implements OnInit {
  public studentId = '';
  public studentInfo: HouseholdStudent | null = null;
  public userRole: UserRole = 'admin';
  public studentName = '';
  public studentOs!: StudentOs;
  public academicYearId: number | null = null;

  private readonly fb = inject(FormBuilder);
  private readonly studentAwardService = inject(StudentAwardService);
  private readonly spinnerService = inject(LoadingSpinnerService);
  private readonly countiesService = inject(CountiesService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly appInsightsService = inject(AppInsightsService);

  readonly sections = SOS_SECTIONS;
  public options: Record<string, FieldOption[]> = { ...SOS_OPTIONS};
  readonly form: FormGroup = this.fb.group({
    ...SOS_SECTIONS.flatMap((s) => s.fields).reduce<
      Record<string, [{ value: null; disabled: boolean } | null, ValidatorFn[]]>
    >(
      (acc, field) => ({
        ...acc,
        [field.key]: [field.readonly ? { value: null, disabled: true } : null, field.validators ?? []]
      }),
      {}
    ),
    isAwarded: [false]
  });

  ngOnInit(): void {
     const studentData = this.route.parent?.snapshot.data['student'] ?? this.route.snapshot.data['student'];
    this.studentInfo = studentData?.detail as HouseholdStudent | null;
    this.studentId = this.route.snapshot.paramMap.get('studentId') ?? '';
    this.academicYearId = this.parseAcademicYearId(this.route.snapshot.queryParamMap.get('academicYearId'));
    this.studentName = this.studentInfo
      ? [this.studentInfo.firstName, this.studentInfo.middleName, this.studentInfo.lastName].filter(Boolean).join(' ')
      : '';
    this.studentOs = studentData?.os as StudentOs;
    const isAwarded = isAwardStatus(this.studentOs?.osAwardStatus, AwardStatus.Accepted, AwardStatus.Awarded);
    this.form.patchValue({ ...this.studentOs, isAwarded });
    this.loadCounties();
    this.applyFosterStatusRule();
    requireReasonWhenIneligible(this.form, 'osApplicationStatus', 'osIneligibilityReasonId', this.destroyRef);
    this.options = {
      ...this.options,
      inEligibilityReasons: [
        { label: 'None', value: null },
        ...(studentData?.inEligibilityReasons ?? []).map((reason: IneligibilityReason) => (
          { label: reason.name, value: reason.id })) ?? []
      ]
    }
  }

  private loadCounties(): void {
    this.countiesService
      .getCountiesByState('NC')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (counties) => {
          this.options['ncCounties'] = counties.map((c) => ({ label: c.name, value: c.name }));
        },
        error: (error: unknown) => this.appInsightsService.log(error instanceof Error ? error : new Error(String(error)))
      });
  }

  private applyFosterStatusRule(): void {
    this.form
      .get('isFoster')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isFoster) => {
        const validators = isFoster === true ? [Validators.required] : [];
        ['dhhsRelease', 'dhhsCaseManager', 'dhhsCounty'].forEach((key) => {
          const ctrl = this.form.get(key);
          if (!ctrl) return;
          ctrl.setValidators(validators);
          ctrl.updateValueAndValidity({ emitEvent: false });
        });
      });
  }

  onCancel(): void {
    if (this.academicYearId == null) {
      this.router.navigate(['/student', this.studentId], {
        info: {
          skipResolve: true,
          studentData: this.route.parent?.snapshot.data['student']
        }
      });
    } else {
      this.router.navigate(['/student', this.studentId], {
        info: {
          skipResolve: true,
          studentData: this.route.parent?.snapshot.data['student']
        },
        queryParams: { academicYearId: this.academicYearId }
      });
    }
  }

  onSave(): void {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      const payload = this.form.getRawValue() as StudentOs;

      if (payload.isFoster !== true) {
        payload.dhhsRelease = null;
        payload.dhhsCaseManager = null;
        payload.dhhsCounty = null;
      }

      if (payload.osApplicationStatus !== ApplicationStatus.Ineligible) {
        payload.osIneligibilityReasonId = null;
      }

      confirmIneligibleTaskCancellation(this.confirmDialog, this.studentOs?.osApplicationStatus, payload.osApplicationStatus, 'Opportunity Scholarship')
        .pipe(filter(Boolean), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.persist(omitUnchangedEligibility(payload, 'osApplicationStatus', 'osIneligibilityReasonId', this.studentOs)));
    }
  }

  private persist(payload: StudentOs): void {
    this.spinnerService.showSpinner();
    this.studentAwardService
      .saveOsStudentDetail(this.studentId, payload, this.academicYearId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.studentAwardService
            .getOs(this.studentId, this.academicYearId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (os) => {
              this.spinnerService.hideSpinner();
              if(this.academicYearId == null) {
                this.router.navigate(['/student', this.studentId], {
                  info: {
                    skipResolve: true,
                    studentData: withSavedOs(this.route.parent?.snapshot.data['student'], os)
                  }
                });
              }
              else{
                this.router.navigate(['/student', this.studentId], {
                  info: {
                    skipResolve: true,
                    studentData: withSavedOs(this.route.parent?.snapshot.data['student'], os)
                  },
                  queryParams: { academicYearId: this.academicYearId }
                });
              }

              },
              error: (error: unknown) => {
                this.spinnerService.hideSpinner();
                this.appInsightsService.log(error instanceof Error ? error : new Error(String(error)));
              }
            });
        },
        error: () => {
          this.spinnerService.hideSpinner();
        }
      });
  }

  private parseAcademicYearId(value: string | null): number | null {
    if (!value) {
      return null;
    }

    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
}
